import { type ComponentProps, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'

import { useSize } from '../useSize'
import { Container, transitionTime } from './Container'
import { Content } from './Content'
import { context } from './context'

const getHeight = (isOpen: boolean, height: number | null) => {
	if (!isOpen) return 0
	if (height === null) return 'auto'
	return height
}

type Props = Omit<ComponentProps<typeof Container>, 'isAnimated'> & {
	isOpen?: boolean
	fill?: boolean
	children: ReactNode
}

export const Collapse = ({ children, isOpen = true, fill, ...rest }: Props) => {
	// Toggle animation on/off when a child element is animating its height
	const [isAnimated, setAnimated] = useState(true)
	// Size of content
	const [ref, { height = null }] = useSize()
	// Track when child transitions should be expected to end
	const [childTransitionEnd, setChildTransitionEnd] = useState<number | null>(null)
	// Notify parent Collapse elements about own transition
	const onTransitionStart = useContext(context)
	// Tracks if height was null last render
	const [isReady, setReady] = useState(false)

	// Run onTransitionStart on height/isOpen changes
	// Should not run on the first measure
	useEffect(() => {
		if (height === null) {
			setReady(false)
		} else if (!isReady) {
			setReady(true)
		} else if (onTransitionStart) {
			onTransitionStart(transitionTime)
		}
		// Only height and isOpen should trigger transition changes
	}, [height, isOpen])

	// Run a timeout everytime a transition-end is set
	useEffect(() => {
		if (childTransitionEnd === null) {
			return
		}
		const timeout = setTimeout(() => {
			setAnimated(true)
			setChildTransitionEnd(null)
		}, childTransitionEnd - Date.now())
		return () => clearTimeout(timeout)
	}, [childTransitionEnd])

	const onChildTransitionStart = useCallback((time: number) => {
		setAnimated(false)
		setChildTransitionEnd(Date.now() + time * 1000)
	}, [])

	return (
		<Container
			fill={fill}
			isAnimated={isAnimated}
			style={{ height: getHeight(isOpen, height) }}
			aria-hidden={!isOpen}
			{...rest}
		>
			<Content ref={ref}>
				<context.Provider value={onChildTransitionStart}>{children}</context.Provider>
			</Content>
		</Container>
	)
}
